import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, Chip, Grid, Paper } from "@mui/material";
import Header from "../../components/Header";
import useAuth from "../../hooks/useAuth";
import StrapiSwitch from "../../components/Switch";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import BackButton from "../../components/BackButton";
import { tokens } from "../../theme";

const DetailField = ({ label, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        backgroundColor: colors.primary[400],
        padding: "16px",
        borderRadius: "8px",
        border: `1px solid ${colors.primary[500]}`,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: colors.grey[600],
          textTransform: "uppercase",
          fontSize: "10px",
          fontWeight: "bold",
          letterSpacing: "1px",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color: colors.grey[100],
          fontWeight: "600",
          wordBreak: "break-all",
        }}
      >
        {value !== null && value !== undefined && value !== "" ? String(value) : "N/A"}
      </Typography>
    </Box>
  );
};

const SwitchField = ({ label, checked, onChange, name }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        backgroundColor: colors.primary[400],
        padding: "12px 16px",
        borderRadius: "8px",
        border: `1px solid ${colors.primary[500]}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: "600" }}>
        {label}
      </Typography>
      <StrapiSwitch checked={checked} name={name} onChange={onChange} />
    </Box>
  );
};

const UpdateUser = () => {
  const { queryUserDetails, snackError, success } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const profile = queryUserDetails?.manageProfile || {};
  const transConfig = queryUserDetails?.transConfig || {};

  // Resolve V3 profile field mapping differences
  const userId = profile.userId || profile.id;
  const firstName = profile.firstName || "N/A";
  const lastName = profile.lastName || profile.surname || "N/A";
  const fullName = profile.fullName || `${firstName} ${lastName}`;
  const email = profile.email || "N/A";
  const phoneNumber = profile.phoneNumber || "N/A";
  const gender = profile.gender || "N/A";
  const dateOfBirth = profile.dateOfBirth ? String(profile.dateOfBirth).split("T")[0] : "N/A";
  const userType = profile.userType || "N/A";
  const status = profile.status || (profile.isActive ? "ACTIVE" : "INACTIVE");
  const kycStatus = profile.kycStatus || "UNVERIFIED";
  const bvn = profile.bvn || "N/A";
  const nin = profile.nin || "N/A";
  const customerId = profile.customerId || "N/A";
  const merchantId = profile.merchantId || "N/A";
  const referralCode = profile.referralCode || profile.referenceCode || "N/A";
  const primaryAccountNumber = profile.primaryAccountNumber || "N/A";
  const tierDisplayName = profile.tierDisplayName || "N/A";
  const dailyTransactionLimit = profile.dailyTransactionLimit !== undefined ? profile.dailyTransactionLimit : "N/A";
  const oneTimeTransferLimit = profile.oneTimeTransferLimit !== undefined ? profile.oneTimeTransferLimit : "N/A";

  const isCorporate = userType === "CORPORATE";

  // Organisation details
  const businessType = profile.businessType || profile.otherDetails?.businessType || "N/A";
  const businessName = profile.businessName || profile.otherDetails?.organisationName || "N/A";
  const streetAddress = profile.streetAddress || profile.otherDetails?.organizationAddress || "N/A";
  const city = profile.city || "N/A";
  const state = profile.state || "N/A";
  const country = profile.country || "N/A";
  const cacNumber = profile.cacNumber || "N/A";
  const organisationEmail = profile.organisationEmail || profile.otherDetails?.organisationEmail || email;
  const organisationPhone = profile.organisationPhone || profile.otherDetails?.organisationPhone || phoneNumber;

  const [switches, setSwitches] = useState({
    smsAlertConfig: false,
    billPayments: false,
    withdrawal: false,
    deposit: false,
    isActive: false,
  });

  useEffect(() => {
    setSwitches({
      smsAlertConfig: !!profile.smsAlertConfig,
      billPayments: !!transConfig.billsPayment,
      withdrawal: !!transConfig.withdrawal,
      deposit: !!transConfig.deposit,
      isActive: profile.isActive !== undefined ? !!profile.isActive : (profile.status === "ACTIVE" || profile.isActive === true),
    });
  }, [profile, transConfig]);

  const handleCheckChange = async (e) => {
    const { name } = e.target;
    const currentValue = switches[name];
    const newValue = !currentValue;

    if (name === "smsAlertConfig") {
      try {
        const response = await axiosPrivate.post(
          "/auth-service/api/v1/admin/users/subscribe-sms-alert",
          {
            phoneNumber,
          }
        );
        setSwitches((prev) => ({ ...prev, [name]: !!response?.data?.data?.active }));
        success(response?.data?.message || "SMS Alert toggled successfully");
      } catch (error) {
        console.log(error);
        snackError(error?.response?.data?.message || "Failed to toggle SMS alert");
      }
    } else if (name === "billPayments") {
      try {
        const response = await axiosPrivate.post(
          "/temporal-service/api/v1/wallet/toggle/" + userId,
          {
            phoneNumber,
          },
          {
            params: {
              type: "Bills",
            },
          }
        );
        setSwitches((prev) => ({ ...prev, [name]: !!response?.data?.data?.billsPayment }));
        success(response?.data?.message || "Bills Payment toggled successfully");
      } catch (error) {
        console.log(error);
        snackError(error?.response?.data?.message || "Failed to toggle Bills Payment");
      }
    } else if (name === "withdrawal") {
      try {
        const response = await axiosPrivate.post(
          "/temporal-service/api/v1/wallet/toggle/" + userId,
          {
            phoneNumber,
          },
          {
            params: {
              type: "Withdrawal",
            },
          }
        );
        setSwitches((prev) => ({ ...prev, [name]: !!response?.data?.data?.withdrawal }));
        success(response?.data?.message || "Withdrawal toggled successfully");
      } catch (error) {
        console.log(error);
        snackError(error?.response?.data?.message || "Failed to toggle Withdrawal");
      }
    } else if (name === "deposit") {
      try {
        const response = await axiosPrivate.post(
          "/temporal-service/api/v1/wallet/toggle/" + userId,
          {
            phoneNumber,
          },
          {
            params: {
              type: "Deposit",
            },
          }
        );
        setSwitches((prev) => ({ ...prev, [name]: !!response?.data?.data?.deposit }));
        success(response?.data?.message || "Deposit toggled successfully");
      } catch (error) {
        console.log(error);
        snackError(error?.response?.data?.message || "Failed to toggle Deposit");
      }
    } else if (name === "isActive") {
      try {
        const response = await axiosPrivate.put(
          `/auth-serviceV3/admin/users/${userId}/active`,
          {},
          {
            params: {
              isActive: newValue,
              reason: "Administrative Action",
            },
          }
        );
        setSwitches((prev) => ({ ...prev, [name]: newValue }));
        if (queryUserDetails?.manageProfile) {
          queryUserDetails.manageProfile.isActive = newValue;
          queryUserDetails.manageProfile.status = newValue ? "ACTIVE" : "INACTIVE";
        }
        success(response?.data?.message || `User successfully ${newValue ? "activated" : "deactivated"}`);
      } catch (error) {
        console.log(error);
        snackError(error?.response?.data?.message || "Failed to toggle account activation status");
      }
    }
  };

  const getStatusColor = (val) => {
    const uppercaseVal = String(val).toUpperCase();
    if (uppercaseVal === "ACTIVE" || uppercaseVal === "VERIFIED" || uppercaseVal === "TRUE") {
      return "success";
    }
    if (uppercaseVal === "PENDING") {
      return "warning";
    }
    return "error";
  };

  return (
    <Box m="20px">
      <BackButton path="/" />
      
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
        <Header title="USER DETAILS" subtitle="View details of this user profile" />
      </Box>

      {/* Profile Summary Card */}
      <Paper
        elevation={3}
        sx={{
          backgroundColor: colors.primary[400],
          padding: "24px",
          borderRadius: "12px",
          marginBottom: "24px",
          border: `1px solid ${colors.primary[500]}`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h2" sx={{ fontWeight: "bold", color: colors.grey[100], mb: "4px" }}>
              {fullName}
            </Typography>
            <Typography variant="h5" sx={{ color: colors.orangeAccent[500], mb: "12px", fontWeight: "500" }}>
              {userType} {isCorporate && businessType ? `(${businessType.replace(/_/g, " ")})` : ""}
            </Typography>
            <Box display="flex" gap="10px" flexWrap="wrap">
              <Chip
                label={`Status: ${status}`}
                color={getStatusColor(status)}
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`KYC: ${kycStatus}`}
                color={getStatusColor(kycStatus)}
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              {profile.tierDisplayName && (
                <Chip
                  label={profile.tierDisplayName}
                  color="secondary"
                  variant="outlined"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Typography variant="body2" sx={{ color: colors.grey[600] }}>
              User ID: <span style={{ color: colors.grey[100], fontWeight: "bold" }}>{userId}</span>
            </Typography>
            {customerId && customerId !== "N/A" && (
              <Typography variant="body2" sx={{ color: colors.grey[600], mt: "4px" }}>
                Customer ID: <span style={{ color: colors.grey[100], fontWeight: "bold" }}>{customerId}</span>
              </Typography>
            )}
            {merchantId && merchantId !== "N/A" && (
              <Typography variant="body2" sx={{ color: colors.grey[600], mt: "4px" }}>
                Merchant ID: <span style={{ color: colors.grey[100], fontWeight: "bold" }}>{merchantId}</span>
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Details Sections */}
      <Grid container spacing={3}>
        {/* Personal Details */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap="16px">
            <Typography variant="h3" sx={{ color: colors.grey[500], fontWeight: "bold", mb: "4px" }}>
              PERSONAL DETAILS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DetailField label="First Name" value={firstName} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Last Name" value={lastName} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Gender" value={gender} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Date of Birth" value={dateOfBirth} />
              </Grid>
              <Grid item xs={12}>
                <DetailField label="Email" value={email} />
              </Grid>
              <Grid item xs={12}>
                <DetailField label="Phone Number" value={phoneNumber} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="BVN" value={bvn} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="NIN" value={nin} />
              </Grid>
              <Grid item xs={12}>
                <DetailField label="Referral Code" value={referralCode} />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Transaction Limits & Toggles */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap="16px">
            <Typography variant="h3" sx={{ color: colors.grey[500], fontWeight: "bold", mb: "4px" }}>
              TRANSACTION LIMITS & SETTINGS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DetailField label="Primary Account Number" value={primaryAccountNumber} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Daily Limit" value={dailyTransactionLimit} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="One-Time Limit" value={oneTimeTransferLimit} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ color: colors.grey[500], fontWeight: "bold", mt: "12px", mb: "8px" }}>
                  ADMINISTRATIVE CONFIGURATION
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <SwitchField
                  label="Account Active"
                  name="isActive"
                  checked={switches.isActive}
                  onChange={handleCheckChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ color: colors.grey[500], fontWeight: "bold", mt: "12px", mb: "8px" }}>
                  TRANSACTION CONFIGURATION
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SwitchField
                  label="SMS Alerts"
                  name="smsAlertConfig"
                  checked={switches.smsAlertConfig}
                  onChange={handleCheckChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SwitchField
                  label="Bills Payments"
                  name="billPayments"
                  checked={switches.billPayments}
                  onChange={handleCheckChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SwitchField
                  label="Withdrawal"
                  name="withdrawal"
                  checked={switches.withdrawal}
                  onChange={handleCheckChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SwitchField
                  label="Deposit"
                  name="deposit"
                  checked={switches.deposit}
                  onChange={handleCheckChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Corporate Details Section */}
        {isCorporate && (
          <Grid item xs={12}>
            <Box display="flex" flexDirection="column" gap="16px" mt="12px">
              <Typography variant="h3" sx={{ color: colors.grey[500], fontWeight: "bold", mb: "4px" }}>
                ORGANIZATION DETAILS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="Organisation Name" value={businessName} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="Organisation Email" value={organisationEmail} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="Organisation Phone Number" value={organisationPhone} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="CAC Number" value={cacNumber} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="Business Type" value={businessType.replace(/_/g, " ")} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailField label="Country" value={country} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailField label="State / City" value={`${state} / ${city}`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailField label="Street Address" value={streetAddress} />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UpdateUser;
